// ============================================================================
// PhoneAttachments — les pieces jointes telles qu'elles paraissent au fil
// ============================================================================
// Rendu dans la carte, par l'emplacement `children` de PublicationCard (sous le
// pied) : @gs/ui n'a aucun emplacement de pieces jointes entre le corps et le
// pied — sa promotion est une question fermee du rapport de la maquette, pas un
// ajout au paquet. Les images en mosaique de vignettes, les documents en cartes
// (icone teintee, nom, genre et taille). Le MEME rendu sert l'apercu du
// telephone (fichiers locaux) et le fil (FeedAttachments, fichiers servis par
// le proxy) : ce que l'on voit en ecrivant est ce qui paraitra. Au fil, un
// document est un bouton qui le telecharge (`onDownload`).

import type { TFunction } from 'i18next';
import { Download, Paperclip } from 'lucide-react';
import { AttachmentTile } from './AttachmentParts';
import { attachmentMeta } from './attachmentLabels';
import type { AttachmentView } from './piecesJointes';

/** La carte d'un document : la meme, qu'elle soit un bouton (fil) ou non (apercu). */
const DOCUMENT_BOX = 'flex items-center gap-2.5 rounded-lg border border-slate-200 px-2.5 py-2 dark:border-slate-700';

export function PhoneAttachments({
  attachments,
  locale,
  t,
  onDownload,
}: {
  attachments: AttachmentView[];
  locale: string;
  t: TFunction;
  /** Au fil : telecharger un document. Absent dans l'apercu (rien a telecharger). */
  onDownload?: (attachment: AttachmentView) => void;
}) {
  const images = attachments.filter((a) => a.kind === 'image' && a.url);
  const documents = attachments.filter((a) => !(a.kind === 'image' && a.url));
  return (
    <section aria-label={t('gest:accueil.editor.files.previewAria')} className="space-y-2">
      <p className="flex items-center gap-1.5 text-meta font-medium text-slate-500 dark:text-slate-400">
        <Paperclip size={13} aria-hidden />
        {t('gest:accueil.editor.files.count', { count: attachments.length })}
      </p>
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-1.5">
          {images.map((a) => (
            <img
              key={a.id}
              src={a.url}
              alt={a.name}
              className="aspect-square w-full rounded-md border border-slate-100 object-cover dark:border-slate-700"
            />
          ))}
        </div>
      )}
      {documents.length > 0 && (
        <ul className="space-y-1.5">
          {documents.map((a) => {
            const content = (
              <>
                <AttachmentTile attachment={a} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-field font-medium text-slate-700 dark:text-slate-200">{a.name}</p>
                  <p className="text-meta text-slate-400 dark:text-slate-500">{attachmentMeta(a, locale, t)}</p>
                </div>
              </>
            );
            return (
              <li key={a.id}>
                {onDownload && a.kind !== 'image' ? (
                  <button
                    type="button"
                    onClick={() => onDownload(a)}
                    aria-label={t('gest:accueil.feed.downloadAria', { name: a.name })}
                    className={`${DOCUMENT_BOX} w-full touch-manipulation text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/40`}
                  >
                    {content}
                    <Download size={15} aria-hidden className="shrink-0 text-slate-400 dark:text-slate-500" />
                  </button>
                ) : (
                  <div className={DOCUMENT_BOX}>{content}</div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
